import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Save, Lock, Plus, Trash2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';

const TEXT_FIELDS = [
  { key: 'objetivo_cuenta', label: 'Objetivo de la cuenta', hint: 'Visibilidad, comunidad, ventas, autoridad…' },
  { key: 'tono_estilo', label: 'Tono y estilo de comunicación' },
  { key: 'frecuencia_formatos', label: 'Frecuencia y formatos', hint: 'Cuántos posts/reels/stories por semana y de qué tipo' },
  { key: 'lineas_rojas', label: 'Líneas rojas', hint: 'Qué no se publica nunca' },
  { key: 'objetivos_numericos', label: 'Objetivos numéricos', hint: 'Seguidores, alcance, interacción, a X meses' },
  { key: 'notas', label: 'Notas de estrategia', hint: 'Campo libre' },
];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function ContentStrategySection({ clientId }) {
  const [data, setData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getStrategy(clientId, 'contenido');
      setData(res?.data || {});
      setCanEdit(!!res?.can_edit);
      setDirty(false);
    } catch { toast.error('Error cargando la estrategia de contenido'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setVal = (key, v) => { setData((p) => ({ ...p, [key]: v })); setDirty(true); };
  const setPilares = (pilares) => { setData((p) => ({ ...p, pilares })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveStrategy(clientId, 'contenido', data); toast.success('Estrategia de contenido guardada'); await load(); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading || !data) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;
  const pilares = Array.isArray(data.pilares) ? data.pilares : [];

  return (
    <Card data-testid="content-strategy-section">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /> Estrategia de Contenido (orgánico)</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Cómo se planifica el contenido orgánico del cliente.</p>
          </div>
          {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
            <Button onClick={handleSave} disabled={saving || !dirty} data-testid="content-strategy-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{dirty ? 'Guardar' : 'Guardado'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label>Objetivo de la cuenta</Label>
          <p className="text-xs text-muted-foreground mb-1">Visibilidad, comunidad, ventas, autoridad…</p>
          <Textarea value={data.objetivo_cuenta || ''} onChange={(e) => setVal('objetivo_cuenta', e.target.value)} rows={2} disabled={RO} className="resize-none" data-testid="content-objetivo_cuenta" />
        </div>

        {/* Pilares de contenido */}
        <div>
          <div className="flex items-center justify-between">
            <Label>Pilares de contenido</Label>
            {!RO && <Button size="sm" variant="outline" onClick={() => setPilares([...pilares, { id: uid(), nombre: '', descripcion: '' }])} data-testid="content-add-pilar"><Plus className="w-4 h-4 mr-1" /> Añadir pilar</Button>}
          </div>
          <p className="text-xs text-muted-foreground mb-2">Los 3-5 temas sobre los que gira todo el contenido.</p>
          {pilares.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin pilares.</p> : (
            <div className="space-y-2">
              {pilares.map((p) => (
                <div key={p.id} className="rounded-lg border p-3 space-y-2" data-testid={`content-pilar-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <Input value={p.nombre || ''} onChange={(e) => setPilares(pilares.map((x) => x.id === p.id ? { ...x, nombre: e.target.value } : x))} placeholder="Nombre del pilar" disabled={RO} className="h-8 font-medium" data-testid={`content-pilar-nombre-${p.id}`} />
                    {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setPilares(pilares.filter((x) => x.id !== p.id))} data-testid={`content-del-pilar-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                  <Textarea value={p.descripcion || ''} onChange={(e) => setPilares(pilares.map((x) => x.id === p.id ? { ...x, descripcion: e.target.value } : x))} placeholder="Descripción" rows={2} disabled={RO} className="resize-none" />
                </div>
              ))}
            </div>
          )}
        </div>

        {TEXT_FIELDS.filter((f) => f.key !== 'objetivo_cuenta').map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
            <Textarea value={data[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} rows={f.key === 'notas' ? 4 : 2} disabled={RO} className="resize-none" data-testid={`content-${f.key}`} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
